// Course-level envelope — the shape of `DraftVersion.content` in Postgres.
//
// Maps onto the `<content>` / `<moduleGroup>` / `<oneModule>` / `<layouts>`
// skeleton in wiki/09_DYNAMO_PLAYER_INTEGRATION.md §5.2.
//
// Course metadata (title, topic, brand, languages) lives on the `course` row;
// **this schema covers only the content body** that ships inside the SCORM
// package (`content/<lang>/content.xml`).
import { z } from "zod";
import { WorkspaceBrandCourseWideSchema } from "./brand.js";
import { LayoutContentSchema } from "./layouts.js";
import { ColorSchema, BuiltInTranslatedStringSchema, DefaultLanguageOnlyStringSchema, LocalizedStringSchema, MediaDescriptionSchema, ProperNounLocalizedStringSchema, UrlSchema, } from "./shared.js";
/**
 * One module — emitted as a `<oneModule id="N">` element.
 *
 * **Defensive optional fields** (added 2026-06-08): `thumbnailUrl`,
 * `duration`, `info`. The Player tolerates their absence (older
 * `20220113_DFTI_V27_S2004` demo omits duration + info and still renders
 * fine), but every newer reference demo (`20251022_MOHG_CoC`,
 * `04_BT_DFTI`) writes all three. Emitting them when populated keeps our
 * SCORM output shape-consistent with the reference corpus and gives
 * authors a place to attach the metadata when they want to.
 *
 * Emit semantics (per `emit-content-xml.ts`): each tag is emitted ONLY
 * when the corresponding field is present in data. Empty tags are NOT
 * emitted (avoids a risk of an empty `<thumbnailURL/>` triggering a 404
 * resolution attempt in the Player's image loader).
 */
export const ModuleSchema = z
    .object({
    /** Stable integer id — emitted as `<oneModule id="N">`. 1-based. */
    id: z.number().int().positive(),
    /** Module title — emitted as `<title><![CDATA[...]]></title>`. */
    title: LocalizedStringSchema,
    /**
     * Optional thumbnail image path — emitted as
     * `<thumbnailURL>...</thumbnailURL>` (note the trailing `URL` uppercase,
     * matching the Player's element name). Relative paths follow the
     * per-field path-prefix convention; reference demos use either
     * within-language paths (`modules/module_1/Mod1.jpg`) or
     * cross-language references (`../en/modules/module_1/Mod1.jpg`).
     * Absent → tag not emitted.
     */
    thumbnailUrl: UrlSchema.optional(),
    /** Screen-reader description — the module tile a learner picks from; the Player already reads it. Per language,
     *  never required, exempt from the translation gate (OQ-095 D1/D2/D7). */
    thumbnailAlt: MediaDescriptionSchema.optional(),
    /**
     * Optional module duration in seconds — emitted as
     * `<duration>N</duration>`. Player reference demos comment the unit
     * inline: `<duration>304</duration><!-- in secunds -->`. Absent → tag
     * not emitted.
     */
    duration: z.number().int().nonnegative().optional(),
    /**
     * Optional one-line module description per language — emitted as
     * `<info><text>...</text></info>` (the nested `<text>` wrapper is
     * load-bearing; confirmed by 3-sample audit of reference content.xml
     * files). Absent → element not emitted.
     */
    info: LocalizedStringSchema.optional(),
    /** The layouts the module contains, in render order. */
    layouts: z.array(LayoutContentSchema).min(1),
})
    .strict();
/** The `<moduleGroup>` wrapper. A course has at least one group. */
export const ModuleGroupSchema = z
    .object({
    /** Group name — emitted on `<moduleGroup name="…">`. */
    name: LocalizedStringSchema,
    /** Brand-accent colour applied to the group — emitted on `<moduleGroup color="…">`. */
    color: ColorSchema,
    /** Modules in this group, in render order. */
    modules: z.array(ModuleSchema).min(1),
})
    .strict();
/**
 * DFTI ("Don't Feed The 'Ish") gaming-flow course-level slice.
 *
 * Surfaces in `config.xml` as `<dfti_flow>` + `<dfti_leftImg>` +
 * `<dfti_rightImg>` (top-level under `<mainSettings>`), and in
 * `content/<lang>/UI.xml` as `<label_profileSetup*>` + `<label_dftiInfo*>`
 * entries the Player reads to render the user-identification Profile-form
 * screen and the DFTI intro screen.
 *
 * Authored on Tab 4 of the `quiz_gaming` layout editor per
 * `wiki/Quiz_layouts_options.md` §11.4 + §13 (Omar's locked-in design
 * 2026-06-03). Course-level — every `quiz_gaming` layout in the course
 * shares the same Profile-form configuration; the Authoring is expected to
 * set `enabled: true` automatically when any `quiz_gaming` layout exists.
 *
 * Field naming mirrors prototype `src/data.jsx` lines 443-454 exactly so
 * the prototype's existing shape round-trips through validation without
 * key rewrites. CLAUDE.md §23.2 still applies — author-facing copy never
 * says "DFTI"; internal data fields use this `dfti*` naming.
 */
export const DftiFlowSchema = z
    .object({
    /**
     * Whether the DFTI gaming flow is on for this course. When `true`,
     * config.xml emits `<dfti_flow>1</dfti_flow>` and the Player shows
     * the Profile-form screen at course start.
     */
    enabled: z.boolean(),
    /** Profile-form instruction body. Emitted as `<label_profileSetupDescription>`. */
    profileSetupDescription: LocalizedStringSchema,
    /** Profile-form first-name field label. Emitted as `<label_profileSetupFirstName>`. */
    profileSetupFirstName: LocalizedStringSchema,
    /** Profile-form last-name field label. Emitted as `<label_profileSetupLastName>`. */
    profileSetupLastName: LocalizedStringSchema,
    /** Profile-form email field label. Emitted as `<label_profileSetupEmail>`. */
    profileSetupEmail: LocalizedStringSchema,
    /** DFTI intro-screen title. Emitted as `<label_dftiInfoTitle>`. */
    dftiInfoTitle: LocalizedStringSchema,
    /** DFTI intro-screen left text (e.g. "Helpful systems work for you"). Emitted as `<label_dftiInfoLeftText>`. */
    dftiInfoLeftText: LocalizedStringSchema,
    /** DFTI intro-screen right text (e.g. "Attackers try to trick you"). Emitted as `<label_dftiInfoRightText>`. */
    dftiInfoRightText: LocalizedStringSchema,
    /**
     * URL of the left intro-screen image (e.g. the friendly-robot icon).
     * **Bare path — no `content/` prefix.** Per
     * `feedback_player_path_prefix_per_field`, the Player auto-prefixes
     * `content/` to this field at runtime (note: `content/` only, NOT
     * `content/<lang>/` — DFTI intro images aren't per-language). Emitter
     * passes through verbatim. Example: `dfti/images/robot_icon_big.png`
     * (NOT `content/dfti/images/robot_icon_big.png` — that would produce
     * a `content/content/...` 404 at runtime). Emitted as `<dfti_leftImg>`.
     */
    dftiLeftImg: z.string(),
    /** Screen-reader description — the gaming intro artwork, which rendered with no description at all. Per language,
     *  never required, exempt from the translation gate (OQ-095 D1/D2/D7). */
    dftiLeftImgAlt: MediaDescriptionSchema.optional(),
    /**
     * URL of the right intro-screen image (e.g. the hacker icon). **Bare
     * path — same auto-prefix rule as `dftiLeftImg` above.** Emitted as
     * `<dfti_rightImg>`.
     */
    dftiRightImg: z.string(),
    /** Screen-reader description — the gaming intro artwork, which rendered with no description at all. Per language,
     *  never required, exempt from the translation gate (OQ-095 D1/D2/D7). */
    dftiRightImgAlt: MediaDescriptionSchema.optional(),
})
    .strict();
/**
 * ONE authoring Role — a learner persona ("Nurse", "Manager") that sees a
 * subset of the course.
 *
 * Scope is Omar's 2026-08-07 decision (wiki `Roles_and_content_mapping_design`
 * §2 Q1): a v1 Role carries a code, a label per language, and the modules it
 * sees. Per-question assessment gating arrives in Phase 3 and lives on the
 * question, not here. Deliberately ABSENT even though the Player supports
 * them: `skipPreAssessment` and `uiOverride` (out of Q1's scope), plus
 * `roleCode`, `brand`, `extranetCode`, the four `imgUrl*` fields and
 * `introVideoUrl` — each is either ignored by the pinned runtime or switched
 * off by another value the emitter writes. Evidence per field:
 * wiki/findings/2026-08-06-roles-mapping-runtime-verification.md §1.
 */
export const RoleSchema = z
    .object({
    /**
     * Stable slug, emitted as the `code` ATTRIBUTE of `<role>`.
     *
     * ⚠️ An ATTRIBUTE, never a child element. It was emitted as `<code>` until
     * 2026-07-28 and left `selected_role` undefined, hanging the Player on
     * "Loading, please wait" (PR #88).
     *
     * The pattern is deliberately narrow because this value becomes part of a
     * URL path — the Player builds
     * `content/0.roles/UI_override/<lang>/UI_<code>.xml` from it
     * (`js/scripts.js:1590`).
     */
    code: z
        .string()
        .regex(/^[a-z][a-z0-9_]{1,31}$/, "role code must be 2-32 chars, lowercase, starting with a letter (a-z, 0-9, _)"),
    /**
     * Display name per language, emitted as one
     * `<label_text language="xx">` child per enabled language.
     *
     * ★ Load-bearing far beyond display, which is why the emitter treats a
     * missing language as an ERROR rather than a warning. The Player builds
     * `roleObject.langagesCode` purely from these `language` attributes
     * (`js/scripts.js:903-907`) and only builds modules for languages in that
     * list; and `countRolesForSelectedLanguage` counts only roles holding a
     * label in the CURRENT language, so a role missing an `it` label makes the
     * Italian role count 1 → the picker is force-disabled
     * (`js/scripts.js:7128-7131`) and the remaining role is auto-selected. An
     * Italian learner would be placed in a persona nobody chose, with nothing
     * on screen to show it.
     */
    label: LocalizedStringSchema,
    /**
     * Module ids this Role sees, emitted as
     * `<modules_list>1,2,4</modules_list>`.
     *
     * ⚠️ Emitted as bare commas with NO spaces — the Player's `split(',')` has
     * no `.trim()` (`js/scripts.js:5945`), so `"1, 2"` excludes module 2.
     * Matching against `<oneModule id>` is string-exact, never substring.
     *
     * An EMPTY array means the Role sees NO module — Omar, 2026-10-13: *"a
     * role with no modules ticked sees no modules, not all of them. No block,
     * no message."* (replacing §2 Q2's "allow it, just warn"). ⚠️ The Player
     * reads an empty `<modules_list>` — and `0` — as EVERY module
     * (`+modulesList[0] === 0`, `scripts.js:10098`), so the emitter cannot
     * write it empty: it writes `none`, which matches no module, plus the
     * role's own `<skip_pre_a>1</skip_pre_a>`, without which a pre-assessment
     * strands that learner on "1/0" (measured in Chrome).
     *
     * Ids that match no module are NOT rejected here, deliberately: `PUT
     * /draft` is all-or-nothing, so blocking would 400 an author's entire
     * course after an ordinary module delete. The emitter drops them and the
     * Player's exact matching ignores them harmlessly.
     */
    moduleIds: z.array(z.number().int().positive()),
    /**
     * The Organisation this Role belongs to, emitted as the `brand` ATTRIBUTE
     * of `<role>` (`js/scripts.js:893` reads it as one).
     *
     * ★ OPTIONAL, and that is load-bearing rather than lenient. A course with
     * no Organisations must emit byte-identically to how it did before
     * Organisations existed — no attribute at all — because every course that
     * exists today is in that state. `undefined` is therefore the *normal*
     * value, not a gap.
     *
     * ⚠️ But it is NOT optional once Organisations exist, and the reason is a
     * silent disappearance rather than an error. When `<selectBrand>1</…>` is
     * emitted, the Player's role picker skips any role whose `brand` does not
     * equal the learner's choice — `roleObj[0].brand !== selected_brand`
     * (`:3948`) — and `undefined` never equals a real code. So an unassigned
     * role vanishes from the picker with nothing on screen to say why. That
     * pairing (must reference a real Organisation, but only when there are any)
     * is a property of the whole `courseSettings`. It was refused at export
     * until Omar removed every content check (2026-10-13); such a role is
     * now simply offered to nobody, which Course preview shows.
     *
     * ★ NULLABLE as well as optional, and that is not laxity. The editor's own
     * representation of "unassigned" is `null`: `surface-org-roles.jsx`'s
     * `addRole` defaults to it while the course has no organisations, and
     * `removeBrand` sets the roles to it when the LAST organisation is deleted
     * (since 2026-10-02 the screen never leaves a role unassigned while
     * organisations remain — deleting one of several asks where its roles go).
     * The projection currently drops falsy
     * values, so a `null` does not reach the server today — but that is the
     * projection's behaviour, not a guarantee, and while `brand` was
     * `string | undefined` a single `null` slipping through returned 400 for the
     * author's ENTIRE course, unrelated edits included. Found by an E2E harness
     * sending the editor's real shape rather than the projection's output.
     * `undefined` and `null` mean the same thing here, deliberately.
     */
    brand: z.string().nullable().optional(),
})
    .strict();
/**
 * The course's Roles, with the one invariant the Player's own matcher forces
 * on us.
 *
 * ★ WHY THE SUBSTRING CHECK EXISTS. Per-question role gating (Phase 3) is read
 * by the Player as `listOfRoles.indexOf(selectedRole) != -1`
 * (`js/scripts.js:1148`) — a SUBSTRING test on the raw attribute string, not a
 * token comparison. So a role coded `hr` silently matches
 * `roles="chr,finance"`, and `manager` matches `senior_manager`. We cannot fix
 * the runtime, so the constraint has to live here: no code may be a substring
 * of another code in the same course.
 *
 * Checked on the ARRAY rather than on `RoleSchema`, because it is a property of
 * the set — a single role can never violate it.
 *
 * ★ `all` IS RESERVED, for the same kind of reason (added 2026-08-10 with
 * per-question gating). `roles="all"` on a `<question>` is the Player's sentinel
 * for "every role", and it is tested BEFORE any code comparison
 * (`scripts.js:9262`). A role literally coded `all` would make its own
 * exclusive selection indistinguishable from the sentinel, so every learner
 * would be asked that role's private questions. Unlike the substring rule this
 * one holds for a SINGLE role, but it lives here so both role-code constraints
 * forced by the Player's matcher are read together.
 */
export const RolesSchema = z.array(RoleSchema).superRefine((roles, ctx) => {
    const seen = new Set();
    roles.forEach((role, index) => {
        if (seen.has(role.code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "code"],
                message: `duplicate role code "${role.code}"`,
            });
        }
        seen.add(role.code);
    });
    // Case-insensitive: the runtime lowercases both sides before comparing
    // (`scripts.js:1202-1203`), so `All` collides with the sentinel just as `all`
    // does. The pattern on `RoleSchema.code` already forbids uppercase, but this
    // check must not depend on that staying true.
    roles.forEach((role, index) => {
        if (role.code.toLowerCase() === "all") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "code"],
                message: `role code "${role.code}" is reserved. The player uses roles="all" on ` +
                    `an assessment question to mean "every role", and tests for it before ` +
                    `comparing role codes, so a role named "all" would have its own ` +
                    `questions shown to every learner. Rename it.`,
            });
        }
    });
    roles.forEach((role, index) => {
        const collides = roles.find((other) => other.code !== role.code && other.code.includes(role.code));
        if (collides) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "code"],
                message: `role code "${role.code}" is a substring of "${collides.code}". ` +
                    `The Player matches assessment-question roles with a substring test, ` +
                    `so "${role.code}" would also match questions meant only for ` +
                    `"${collides.code}". Rename one of them.`,
            });
        }
    });
});
/**
 * ONE Organisation — a part of the business the learner picks before their
 * role. Emitted as a `<brand>` entry inside `<brands>`.
 *
 * The Player calls this a "brand"; the authoring UI calls it an Organisation.
 * The XML name is the Player's and cannot change; the human-facing name is
 * ours. Both are kept deliberately, rather than renaming one to match the
 * other, because the XML is a contract with a runtime we do not own.
 */
export const BrandSchema = z
    .object({
    /**
     * Stable slug, emitted as the `code` ATTRIBUTE of `<brand>`.
     *
     * Same narrow pattern as `RoleSchema.code`, for a different reason: this
     * value is written into the DOM as `data-brand` on the SCORM container
     * (`js/scripts.js:7111`, `:7693`) for CSS to select on, and it is round
     * tripped through SCORM suspend data on resume (`:14575` → `:12549`).
     *
     * ★ Note which role-code rule does NOT apply here, so nobody adds it by
     * analogy: there is no substring hazard and no reserved word. Organisation
     * matching is exact (`!==` at `:3948`, `===` at `:7136`), and there is no
     * "every organisation" sentinel corresponding to roles' `all`. Copying the
     * roles rules across would reject valid Organisation names for a reason
     * that does not exist.
     */
    code: z
        .string()
        .regex(/^[a-z][a-z0-9_]{1,31}$/, "organisation code must be 2-32 chars, lowercase, starting with a letter (a-z, 0-9, _)"),
    /**
     * Display name per language, emitted as one
     * `<label_text language="xx">` child per enabled language.
     *
     * ★ Mandatory per language, and the failure mode is silence rather than an
     * error: `buildOneBrandImg` renders a pill only when
     * `label_text[selected_language]` is defined (`js/scripts.js:3889`), so an
     * Organisation missing its Italian label simply **is not offered** to an
     * Italian learner — and every role inside it becomes unreachable with it.
     *
     * ⚠️ CORRECTED 2026-08-12 after Omar tested it. This was
     * `LocalizedStringSchema`, which made the coverage gate demand an Italian and
     * Arabic spelling of "Retail" and BLOCKED his export:
     *
     *   > *"the check show the following message that is not necessary as the
     *   > Organisation names do not need to be translated"*
     *
     * He is right, and the reference packages agree — BT's organisations are
     * `EE`, `Plusnet`, `Openreach`. It is now a
     * {@link ProperNounLocalizedStringSchema}, so the author is never asked.
     *
     * ★ The Player's requirement has NOT gone away: it renders a pill only when
     * `label_text[selected_language]` is defined (`js/scripts.js:3889`), so a
     * missing entry hides the organisation from those learners and takes every
     * role inside it with it. The EMITTER therefore fills every enabled language
     * from whichever value exists. Those two changes are one fix and must never
     * be separated.
     */
    label: ProperNounLocalizedStringSchema,
    /**
     * Band colour in the authoring grid. **Authoring-only — never emitted.**
     *
     * Added in Phase 4b. It lives in the persisted slice rather than in
     * `authoringState` on purpose: `authoringState` is stored verbatim and
     * unvalidated, so putting the colour there would give one organisation two
     * homes — its identity in `courseSettings.brands` and its appearance
     * somewhere else — which is how two copies of one fact start to diverge
     * (`feedback_one_rule_one_place`). `ModuleGroupSchema.color` sets the
     * precedent: an authoring-side colour already lives in the content schema.
     *
     * ⚠️ It genuinely cannot reach the learner, and that is a runtime limit, not
     * a scope decision: learner theming resolves a CSS FOLDER name via
     * `changeBrandingTo` (`js/scripts.js:7146`) and the pinned runtime ships
     * exactly one, `brand_BT`. A hex has nowhere to go. The UI must say so.
     *
     * A per-organisation LOGO is a different matter and IS supported — see
     * `wiki/Roles_and_content_mapping_design.md` §3.9.4, and Phase 4d.
     */
    color: z.string().optional(),
    /** Text colour paired with `color`. Authoring-only, same reasoning. */
    textColor: z.string().optional(),
    /**
     * OPTIONAL per-organisation logo — `asset://<uuid>`, one image, and it DOES
     * reach the learner. Phase 4d.
     *
     * Omar, 2026-08-12: *"there should be the ability to upload a logo for each
     * Organisation. It shouldn't be mandatory but when I select an organisation
     * that has its own logo, then this logo should be displayed … on the Home top
     * left section."*
     *
     * ⚠️ I had published that this was impossible. It is not, and the mechanism is
     * CSS rather than XML — measured in the pinned runtime:
     *
     *   1. `<dynamicBrandLogo>1</dynamicBrandLogo>` → `contentSetup.dynamic_brand_logo`
     *      (`js/scripts.js:6524`), which adds the `dynamicBrandLogo` class to
     *      `#SCORM_Container` at eight points across the learner journey
     *      (`:7133`, `:7379`, `:7502`, `:7548`, `:7646`, `:7720`, `:7778`, `:7804`);
     *   2. the learner's choice is written to the DOM as `data-brand="<code>"`
     *      (`:7111`, `:7693`, and on resume `:12558`) — which is exactly why
     *      `code` above must stay a stable slug;
     *   3. `css/branding/brand_BT/clientSpecific.css` — @imported unconditionally
     *      from `core_style.css:24` — carries
     *      `#SCORM_Container.dynamicBrandLogo[data-brand="ee"] #eLHeaderLogo { background-image: … }`
     *      for four organisations in the live BT package (`:179-198`);
     *   4. the two targets are `#eLHeaderLogo` (header, top-left — visible on Home)
     *      and `#homeInfoCompanyLogo` (the Home panel, `components.js:68`).
     *
     * ★ ONE image, not four. The old mock offered four `logoVariants`, a shape
     * inherited from `<brand>`'s XML logo fields — which feed only COMMENTED-OUT
     * pill code. The runtime has exactly two live slots, both `background-size:
     * contain`, and the shipped BT package already points ONE file at BOTH of them
     * for Openreach (`clientSpecific.css:197-199`). So the precedent for "one logo
     * fills both" is the production package itself
     * (`feedback_one_door_per_field_reuse_existing_style`).
     *
     * ⚠️ NOT a member of any `asset-inline.ts` field set, deliberately. All four
     * of its placements put the file under `content/…` and rewrite a value read
     * from XML. This value is read from a STYLESHEET and the file must land beside
     * that stylesheet, so the packager handles it directly. The generic
     * `findUninlinedAssetRefs` backstop still covers it: an `asset://` left here
     * unrewritten fails the export naming `courseSettings.brands[N].logo` —
     * verified, not assumed — so a half-wired logo can never ship as a broken
     * path.
     */
    logo: z.string().optional(),
})
    .strict();
/**
 * The course's Organisations. Set-level rules only — the cross-slice rules
 * (every role in a real Organisation, every Organisation holding a role) need
 * to see the roles too, so they live on `CourseSettingsSchema`.
 */
export const BrandsSchema = z.array(BrandSchema).superRefine((brands, ctx) => {
    const seen = new Set();
    brands.forEach((brand, index) => {
        if (seen.has(brand.code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "code"],
                message: `duplicate organisation code "${brand.code}"`,
            });
        }
        seen.add(brand.code);
    });
});
/**
 * Course-level settings — author-managed config that doesn't fit the
 * per-layout content model. Optional on `DraftContent`; absent in courses
 * that don't use DFTI or other settings-flagged features.
 *
 * Reserved for slices that affect course-wide behaviour (DFTI flow,
 * starting-cover, language toggles, role gating, etc.). Per-layout
 * content stays inside `LayoutContent`.
 */
/**
 * ONE answer option on a pre-/post-assessment question.
 *
 * Emitted as `<answer id="N" [correct="true"]><![CDATA[…]]></answer>`.
 * The `id` is NOT stored — the emitter assigns it from array position (1-based)
 * so it always matches the `for="N"` on this answer's `<wrong_fb>`. Reference
 * packages never write `correct="false"` (0 occurrences across 5,172 answers),
 * so the attribute is emitted only for correct options.
 */
export const AssessmentAnswerSchema = z
    .object({
    /** Answer text — emitted as the `<answer>` element's CDATA body. */
    text: LocalizedStringSchema,
    /** Marks this option correct. Emitted as `correct="true"`; omitted when false. */
    correct: z.boolean().optional(),
    /**
     * Feedback shown when the learner picks THIS wrong option — emitted as
     * `<wrong_fb for="N">`. Omit for a single shared wrong-answer message
     * (the Player branches on element COUNT: exactly one `<wrong_fb>` is
     * treated as generic regardless of its `for` attribute — `scripts.js`
     * 1192-1207).
     */
    wrongFeedback: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * ONE pre-/post-assessment question. Emitted as
 * `<question id="N" roles="all" type="radio|checkbox">`.
 *
 * `type` is DERIVED, never authored: one correct answer → `radio`, more than
 * one → `checkbox`. Those are the only two values the pinned Player's render
 * switch handles (`scripts.js:4339-4381`); anything else falls through and the
 * question disappears silently.
 *
 * ★ A `roles` ATTRIBUTE IS ALWAYS EMITTED, for three independent runtime
 * reasons — two long-known, the third measured 2026-08-10 when per-question
 * gating was built (Stage 5 Phase 3a):
 *
 *  1. `getPostAssessmentQuizModulesList` does `attr('roles').split(',')`
 *     unguarded (`scripts.js:10900`); with no attribute that is
 *     `undefined.split`, killing the post-assessment launch button.
 *  2. `newGroupObj` tolerates a missing attribute (`:1204`), so parser-level
 *     tests do NOT catch reason 1.
 *  3. ★ THE PLAYER FILTERS THE SAME SET TWICE, AND THE SECOND PASS THROWS ON AN
 *     UNTAGGED QUESTION. Parse (`:1202`) coerces with
 *     `String($(this).attr('roles')).toLowerCase()` and admits the result
 *     `'undefined'` — so a question with no attribute gets in — but `:1156`
 *     then stores the RAW attribute, i.e. the real `undefined`. Render
 *     (`:9261`) does `qGroup.questionArr[j].roles.toLowerCase()` on that value
 *     and raises a TypeError, which aborts the `$.each` over the whole
 *     `quizArray` (`:7022`) and leaves the assessment container built but
 *     EMPTY. `getPostAssessmentQuizModulesList` (`:10901`) throws first on the
 *     same input, killing the launch button before the learner gets that far.
 *
 * ⚠️ Reason 3 was documented differently when this field was added, and the
 * correction is recorded rather than quietly swapped: it claimed the untagged
 * question was silently excluded from the draw, over-running the loop into
 * `getRandRange(0, -1)`. That is not what happens — execution never reaches the
 * draw. OBSERVED 2026-08-10 in headless Chrome against the pinned runtime, by
 * stripping the attribute from a built package: two real TypeErrors at
 * `scripts.js:10901` and `:9261`, ending on a stranded "1/0" screen with zero
 * question containers. The consequence is worse than first written, and the
 * mechanism is different; a wrong mechanism in a comment is worse than none.
 *
 * Evidence: wiki/Roles_and_content_mapping_design.md §2 Q1.
 */
export const AssessmentQuestionSchema = z
    .object({
    /** Question stem — emitted as `<questionText><![CDATA[…]]></questionText>`. */
    prompt: LocalizedStringSchema,
    /** Answer options in display order. */
    answers: z.array(AssessmentAnswerSchema),
    /** Shown after a correct response — emitted as `<correct_fb>`. Omit to emit nothing. */
    correctFeedback: LocalizedStringSchema.optional(),
    /**
     * Shared wrong-answer feedback — emitted as a single generic `<wrong_fb>`
     * when no per-answer `wrongFeedback` is authored.
     */
    wrongFeedback: LocalizedStringSchema.optional(),
    /**
     * Which Roles are asked this question — emitted as the `roles` ATTRIBUTE of
     * `<question>`.
     *
     * ★ THE TAG LIVES ON THE QUESTION, and that is a decision rather than a
     * convenience (design doc §3.7). The alternative — a list of question ids
     * on each Role — needs a durable question identity, and there is none: this
     * schema has no id, the frontend's `uid` is stripped by the projection, the
     * emitted `<question id>` is array position, and the AI generator's
     * "regenerate all" re-mints every `uid` (`surface-assessments.jsx:136-154`).
     * A reference would therefore be silently orphaned by a supported action,
     * whereas a tag riding on the question survives copy, delete and reorder
     * with no reconciliation step.
     *
     * Three values, three meanings — `undefined` is NOT the same as `[]`
     * (`feedback_an_empty_list_may_mean_everything` warns about the opposite
     * default one level up, at `Role.moduleIds`, so do not carry that intuition
     * here):
     *
     * | Value | Emitted | Learner effect |
     * |---|---|---|
     * | `undefined` (absent) | `roles="all"` | every Role — the back-compatible default |
     * | `["nurse","manager"]` | `roles="nurse,manager"` | only those Roles |
     * | `[]` | `roles=""` | NOBODY. Inert, not dangerous: both Player filters reject it and the clamp shrinks with it, so it is warned about, never blocked |
     *
     * ⚠️ Never emit `all` mixed with codes. The render filter tests
     * `indexOf(', all')` (`scripts.js:9262`), so `roles="nurse, all"` admits
     * everyone while our bare-comma `roles="nurse,all"` does not. Emit either
     * exactly `all` or a plain list.
     *
     * Codes that match no Role are dropped by the emitter rather than rejected
     * here, for the same reason `Role.moduleIds` tolerates dangling module ids:
     * `PUT /draft` is all-or-nothing, so rejecting would 400 an author's entire
     * course after an ordinary Role rename.
     */
    roles: z.array(z.string()).optional(),
})
    .strict();
/**
 * A question group — the unit that binds assessment questions to ONE module.
 * Emitted as `<questionGroup id="N" module="M" questionsNumbers="K">`.
 *
 * `id` is assigned by the emitter from array position (1-based) and is never
 * `0` or `00`: the Player reserves those two ids for the survey and final
 * survey, stripping them out of the post-assessment (`scripts.js:6829-6843`).
 *
 * `moduleId` MUST equal a `ModuleSchema.id` (the same integer that becomes
 * `<oneModule id="N">`). A group whose module has no match renders zero
 * questions with no error, so the export gate checks it.
 */
export const AssessmentGroupSchema = z
    .object({
    /** The module this group's questions belong to — matches `ModuleSchema.id`. */
    moduleId: z.number().int().positive(),
    /**
     * How many of this group's questions each learner is asked — emitted as
     * `questionsNumbers="K"`. The Player draws K at random from the pool,
     * without replacement, on every attempt (`scripts.js:9271-9276`); the
     * sampling is unconditional and has no off switch. K > pool size is
     * clamped down at emit.
     */
    questionsShown: z.number().int().positive(),
    /** The question pool for this module. */
    questions: z.array(AssessmentQuestionSchema),
})
    .strict();
/**
 * One assessment phase (pre or post). `enabled` drives `<have_pre_a>` /
 * `<have_post_a>` in config.xml — but only together with real content: an
 * enabled phase with no questions emits `0`, because a flag without questions
 * gives the learner an empty screen.
 */
export const AssessmentPhaseSchema = z
    .object({
    /** Author's switch for this phase. */
    enabled: z.boolean(),
    /** Question groups, one per module the author has added questions for. */
    groups: z.array(AssessmentGroupSchema),
})
    .strict();
/**
 * Course-level pre-/post-assessment slice.
 *
 * **Deliberately permissive** (empty arrays, optional feedback) because
 * `PUT /draft` validates the WHOLE course: one required-but-unfilled
 * assessment field would 400 every layout's save too. Nothing refuses an
 * incomplete one at export either since Omar removed every content check
 * (2026-10-13): the build leaves out a question with no answers and a group
 * whose module is gone (`leaveOutWhatHasNothingForALearner`) and writes the
 * rest as it is.
 *
 * v1 scope notes, each verified against the pinned runtime:
 *  - Pass rule is COMPLETION only (`<postAssessmentType>1</postAssessmentType>`,
 *    what all 5 reference packages ship). Threshold mode is not modelled: its
 *    fail path still reports `cmi.success_status = "passed"` to the LMS
 *    (`scripts.js:8824`), and it silently disables module filtering
 *    (`scripts.js:1113`).
 *  - No per-question media. Per-answer images are parsed but their render
 *    site is commented out in the pinned cut (`scripts.js:4431`).
 *  - No per-question roles — a single `default` role exists today.
 */
export const AssessmentsSchema = z
    .object({
    /** Pre-assessment (diagnostic, shown before the modules). */
    pre: AssessmentPhaseSchema.optional(),
    /** Post-assessment (shown from the module screen once every module is done). */
    post: AssessmentPhaseSchema.optional(),
    /**
     * Shuffle the ORDER in which question groups appear — emitted as
     * `<postAssessmentRand>`. Despite the name this is ONE course-wide flag
     * that the Player applies to BOTH assessments (`scripts.js:7019`, inside
     * the generator shared by pre and post), which is why it lives here and
     * not on a phase. It does not change WHICH questions are drawn; that is
     * always random per learner.
     */
    randomiseGroupOrder: z.boolean().optional(),
})
    .strict();
/**
 * The course's opening screen — the full-bleed image, the course title, one
 * authored sentence and the "Start Course" button the learner presses to enter.
 *
 * The Player calls it the *starting cover* and builds it in
 * `buildStartingCoverScreen()` (`js/scripts.js:3784-3804`). It is the FIRST
 * entry of `segmentsOrder` for every course template we emit (1, 5 and 7 —
 * `:694-704`), so it is shown before the language picker, the organisation
 * picker and the pre-assessment.
 *
 * ★ THE SCREEN IS TEMPLATE-GATED, and it is already satisfied. The config
 * parser does `if (templateArr.indexOf('startingCover') == -1)
 * setup.have_startingCover = false` (`:6570`) — the same silent override that
 * makes `<have_pre_a>` a no-op on template 1. `startingCover` is present in
 * ALL of segmentsOrder[1]…[8], so no template change is needed here. This is
 * the one gated screen we get for free; do not assume the others are.
 *
 * ★ WHAT THIS SLICE DOES NOT OWN, and why. Three of the six things on the
 * cover come from somewhere else, and adding a control here for any of them
 * would be two doors onto one fact (`feedback_one_rule_one_place`):
 *
 *   - The HEADLINE is `SL.UI.course_title` (`:3792`) — the course title, which
 *     already reaches UI.xml as `<label_course_title>` and is the SAME value
 *     the course header (`:3684`) and the browser tab (`:1749`) use.
 *   - The paragraph under it is `SL.UI.course_description` (`:3794`), the
 *     course description shared with the header (`:3688`).
 *   - The BUTTON reads `lable_startCourse` (`:3796` — the misspelling is the
 *     contract, `feedback_an_apparent_typo_may_be_the_contract`), which all 15
 *     bundled UI templates already carry professionally translated.
 *
 * What IS cover-only is {@link CoverSchema.sentence} → `startingCover_additionalText`
 * (`:1294` → `:3788`), read by this screen and nothing else.
 */
export const CoverSchema = z
    .object({
    /**
     * Whether to show the cover at all — emitted as `<have_startingCover>`.
     *
     * Defaults to absent, and absent MUST keep meaning "off": every course
     * authored before this slice existed emitted `<have_startingCover>0`, and
     * switching that on retroactively would put a new screen in front of
     * courses already published.
     */
    enabled: z.boolean(),
    /**
     * Full-bleed background image behind the cover.
     *
     * **Bare path — no `content/` prefix**, because the Player concatenates the
     * literal `'content/'` itself (`:3786`). This is the same auto-prefix rule
     * as `dftiLeftImg`, which is why both are LANGUAGE-INDEPENDENT in the
     * packager rather than PREFIXED or RAW (`asset-inline.ts` header comment).
     *
     * ★ The field is named `coverBgImgUrl`, NOT `bgImgUrl`, deliberately. The
     * packager classifies media by KEY NAME alone (`asset-inline.ts:355-357`),
     * and `bgImgUrl` is already taken by the quiz_gaming screen backgrounds —
     * which are RAW (full path). Reusing the name would silently file the cover
     * image under the opposite convention and 404 as `content/content/…`.
     */
    coverBgImgUrl: z.string().optional(),
    /**
     * Optional logo drawn over the cover, above the title (`#brandLogoContainer`,
     * `:3791`). Same bare-path rule as {@link CoverSchema.coverBgImgUrl}.
     *
     * ★ NOT derived from the learner's organisation, and it cannot be: the
     * `startingCover` segment runs BEFORE `brandSelect` in every template
     * (`:694-704`), so no organisation has been chosen when this screen paints.
     * It is a course-level mark, authored once.
     */
    coverLogoImgUrl: z.string().optional(),
    /** Screen-reader description — the cover LOGO is informative — it says whose course this is (D5).
     *  The cover BACKGROUND is decorative and deliberately has no field. Per language,
     *  never required, exempt from the translation gate (OQ-095 D1/D2/D7). */
    coverLogoAlt: MediaDescriptionSchema.optional(),
    /**
     * One authored sentence shown between the title and the description —
     * emitted into each language's UI.xml as `<startingCover_additionalText>`.
     *
     * The only text on this screen that belongs to the cover alone; everything
     * else it renders is shared with the header (see the class docstring).
     */
    sentence: DefaultLanguageOnlyStringSchema.optional(),
})
    .strict();
/**
 * The organisation News screen — `content/<lang>/news.xml`, one file per
 * enabled language.
 *
 * ★ THE FLAG ALONE DOES NOTHING, which is why this is a content slice and not
 * a boolean on {@link CourseFeaturesSchema}. The Player reaches the screen only
 * when all THREE of these hold (`js/scripts.js:7218`):
 *
 * ```js
 * if (HShell.contentSetup.have_newsScreen &&
 *     typeof HShell.content.newsObj !== 'undefined' &&
 *     HShell.content.newsObj.enabled) { gotoNewsScreen(); }
 * ```
 *
 * `newsObj` is populated by `getNewsXml()` (`:6665`), which reads
 * `content/<code>/news.xml`. Every package we build already ships that file —
 * as `STUB_NEWS_XML`, which carries NO `<enabled>` element, so
 * `!!+$(xml).find('enabled').text()` is `!!+"" === false` and the segment is
 * skipped in silence. So shipping `<have_newsScreen>1</have_newsScreen>` on its
 * own gives the author a switch that changes nothing at all
 * (`feedback_no_false_affordance_toggles`) — the real file has to travel with
 * it, which is what `emitNewsXml` is for.
 *
 * ★ IT IS ALSO SEGMENT-GATED. `newsScreen` is absent from `segmentsOrder[1]`
 * (the Bronze Shell default) and present in every one of [2]…[8]. Unlike
 * `newToCompany` there is no `templateArr.indexOf` line forcing the flag false
 * (`:6568-6572` gates exactly `langSelect`, `newToCompany`, `startingCover`,
 * `roleSelect` and `pre_a`), so on template 1 the flag stays TRUE and the
 * screen is simply never walked to — the failure is a missing screen, not a
 * corrected flag. `emit-config-xml.ts` moves the course to template 7 for this.
 *
 * Shape mirrors the reference packages byte-for-byte (`01_MOHG/content/en/news.xml`):
 * `<news><enabled>1</enabled><title><![CDATA[…]]></title><message><![CDATA[…]]></message></news>`.
 * MOHG ships an EMPTY `<title>` and a long `<message>`, so a blank title is a
 * real authoring state and not a mistake — `title` is optional here for that
 * reason.
 */
export const NewsSchema = z
    .object({
    /**
     * Whether to show the News screen — drives `<have_newsScreen>` AND the
     * `<enabled>` element inside each `news.xml`. Both, because the Player
     * checks both; a course with `have_newsScreen=1` and `<enabled>0</enabled>`
     * skips the screen.
     */
    enabled: z.boolean(),
    /**
     * Heading above the message (`#newsTitleContainer`, `:4026`). Optional:
     * the reference corpus ships it empty.
     */
    title: LocalizedStringSchema.optional(),
    /**
     * The news body (`#newsScreenContent`, `:4027`). Written into the DOM with
     * `.innerHTML` via jQuery string concatenation, so inline markup is
     * intended — MOHG's own message carries `<br/>` tags.
     */
    message: LocalizedStringSchema.optional(),
})
    .strict();
/**
 * The four blocks of prose on the "new to company" intro screen — an OVERRIDE
 * slice, which is what makes it different from every other content slice here.
 *
 * ★ THE TEMPLATES ALREADY CARRY THIS TEXT, professionally translated into all
 * 15 languages. The screen renders four elements (`components.js:7352-7353`):
 *
 * | this field | UI.xml tag                                 | rendered as                  |
 * |------------|--------------------------------------------|------------------------------|
 * | `title`    | `label_role_newToCompanyIT_title`          | `<h2>`                       |
 * | `intro`    | `label_role_newToCompanyIT_purple_content` | `<p class="purple">`, omitted when blank |
 * | `body`     | `label_role_newToCompanyIT_content`        | `<p>`                        |
 * | `footnote` | `label_role_newToCompanyIT_more_content`   | `<p>`                        |
 *
 * ("IT" is the BT *ITMatters* course-variant marker, not Italian — the runtime
 * renders only the IT set; the plain `label_role_newToCompany_*` pair has no
 * render site anywhere.)
 *
 * ★ SO ABSENT MEANS "USE THE PROFESSIONAL DEFAULT", NOT "SHIP BLANK". Every
 * other slice here is the only source of its text, so absent means empty. Here
 * the emitter leaves the template body untouched unless the author wrote
 * something for THAT language — which is why the fill is per-language with no
 * cross-language fallback: falling back to `en` would replace a good Italian
 * default with English, the exact opposite of the intended repair.
 *
 * ★ WHY IT IS AUTHORABLE AT ALL. It was blank until 2026-08-14 — the template
 * generator's `BLANK_FEATURE_TAGS` emptied all four (and `label_full_course`,
 * the "Full course" button's own label) on a premise that expired the day the
 * `newToCompany` toggle shipped. Omar hit it: an unlabelled button under no
 * text. Restoring the defaults fixes the blank screen; this slice exists
 * because he also asked to edit the English and translate it
 * (`feedback_a_screen_that_owns_data_needs_a_save_door` — the panel that shows
 * text must be able to change it).
 *
 * ★ AND A LANGUAGE WITHOUT WORDING OF ITS OWN IS COVERED, NOT MISSING (Omar,
 * 2026-10-08): *"Export must treat a language without its own New to company
 * wording as covered by the app's built-in translation."* So every block is a
 * `BuiltInTranslatedStringSchema` (see BUILT_IN_TRANSLATION_MARKER in shared.ts):
 * the gate never asks for a language, and the main-language fill never copies
 * the English over a built-in translation — after an English change the Italian
 * learner keeps the built-in Italian until someone translates the new English.
 * Until that day an English override made the gate demand every other language.
 * Localisation still offers these fields for translation
 * (`walkCourseTranslatables`), as an invitation rather than a requirement.
 */
export const NewToCompanyIntroSchema = z
    .object({
    title: BuiltInTranslatedStringSchema.optional(),
    intro: BuiltInTranslatedStringSchema.optional(),
    body: BuiltInTranslatedStringSchema.optional(),
    footnote: BuiltInTranslatedStringSchema.optional(),
})
    .strict();
/**
 * Course-wide screen behaviour — the toggles that are a single config.xml flag
 * with no content of their own. Anything needing content gets its own slice
 * (see {@link NewsSchema}).
 *
 * Every field is optional and absent means OFF, so a course authored before
 * this slice existed emits exactly the flags it emitted before.
 */
export const CourseFeaturesSchema = z
    .object({
    /**
     * `<sequential_modules>` — locks each module until the previous one is
     * complete (`js/scripts.js:10268`, `:10661`, `:14573`). No template gate,
     * no content, no companion tag; `04_BT_DFTI` ships it as 1.
     */
    sequentialModules: z.boolean().optional(),
    /**
     * "Block progression" — the course-level DEFAULT for the per-screen progress
     * gate. Author-facing label is exactly that; the key keeps its original name
     * because every saved draft carries it.
     *
     * NOT a config.xml flag — there is no such tag. It is applied on the BUILD
     * path by `resolveBlockingSections`, which writes each layout's
     * `blockingSection.state`; the Player then hides every layout after the
     * first gated one until its Continue button is pressed (`components.js`,
     * `_showLayoutsUntilNextLocked`).
     *
     * ★ IT IS A DEFAULT, NOT AN OVERRIDE (Omar, 2026-09-04). A screen that
     * carries its own explicit `blockingSection.state` keeps it, in either
     * direction; this switch decides only the screens that have no opinion. And
     * it never reaches an INFORMATIONAL layout at all — the five types with no
     * completion condition (`title`, `two_columns_text`, `text_and_image`,
     * `fullscreen_text_and_image`, `object_viewer`) are fixed at `"false"`
     * whatever this says, because each calls `enableContinueButton` outside any
     * handler (`components.js:3096-3128`, `:5246`) so a gate there could never
     * hold anyone and its "Complete the content above before moving on" bar
     * would be a false instruction.
     *
     * `"false"` and not ABSENT, which is a different thing in the Player and was
     * briefly got wrong: absent removes the continue bar entirely, takes the
     * screen's gate colours with it, hides the end-of-module panel, and renders
     * every consecutive screen in one scroll. `"false"` keeps the bar, open from
     * the moment the screen draws — "Click the arrow down to continue learning",
     * which is the behaviour Omar asked to keep. The full table and the
     * measurement live in `./blocking.ts`.
     *
     * Default OFF, and turning it on is an explicit Admin action before the
     * course is exported for the LMS.
     */
    blockingByDefault: z.boolean().optional(),
    /**
     * `<have_newToCompany>` — the "are you new to the company?" screen
     * (`buildIsNewEmployeeScreen`, `:3811`).
     *
     * ★ TEMPLATE-GATED, and NOT satisfied for free: `:6569` does
     * `if (templateArr.indexOf('newToCompany') == -1)
     * setup.have_newToCompanyScreen = false`, and `newToCompany` is absent from
     * segmentsOrder[1]. `emit-config-xml.ts` moves the course to template 7.
     * This is the gate the cover screen got away with; this one does not.
     *
     * ★ ITS ONLY CONSEQUENCE IS THE PRE-ASSESSMENT. Answering "yes" sets
     * `skip_pre_a = true` and `roleNoPreAssessment = true` (`components.js`,
     * `NewToCompany.prototype.onNextClicked`), which is read at `:7183-7186`
     * to decide whether to walk to `pre_a`. With no live pre-assessment both
     * answers lead to the same next screen — the Course settings panel says so
     * rather than letting the author discover it in a package.
     */
    newToCompany: z.boolean().optional(),
})
    .strict();
export const CourseSettingsSchema = z
    .object({
    /** DFTI gaming-flow slice — see `DftiFlowSchema`. */
    dftiFlow: DftiFlowSchema.optional(),
    /**
     * Opening-screen slice — see `CoverSchema`. Absent in every course authored
     * before 2026-08-13, and absent must keep emitting
     * `<have_startingCover>0</have_startingCover>` exactly as before.
     */
    cover: CoverSchema.optional(),
    /**
     * The course title, per language — emitted as `<label_course_title>` into
     * each `content/<lang>/UI.xml`.
     *
     * ★ The `course` ROW's `title` column stays authoritative for everything
     * outside the content tree: the ZIP filename, `imsmanifest.xml`, the
     * courses list. This slice is the same title in each enabled language, and
     * `emit-ui-xml.ts` falls back to the row title for any language it has no
     * entry for — so absent (every course authored before 2026-08-14) keeps
     * emitting one title in every language exactly as before.
     *
     * ★ ONE EDITING DOOR. Course settings is the only screen that writes it,
     * and it writes the default-language entry and the row together; the
     * Localisation surface's Welcome tab writes the other languages. The header
     * breadcrumb used to look editable and was not wired to anything at all —
     * removed 2026-08-14 rather than wired, so there is one place per rule
     * (`feedback_one_rule_one_place`).
     */
    courseTitle: LocalizedStringSchema.optional(),
    /** Organisation News screen — see `NewsSchema`. */
    news: NewsSchema.optional(),
    /** Course-wide screen behaviour — see `CourseFeaturesSchema`. */
    features: CourseFeaturesSchema.optional(),
    /**
     * Author's overrides for the "new to company" intro prose — see
     * `NewToCompanyIntroSchema`. Absent means the professionally-translated
     * template text ships, so this is the one slice where absent is a good
     * state rather than an empty screen.
     */
    newToCompanyIntro: NewToCompanyIntroSchema.optional(),
    /** Pre-/post-assessment slice — see `AssessmentsSchema`. */
    assessments: AssessmentsSchema.optional(),
    /** Role-gating slice — see `RolesSchema`. */
    roles: RolesSchema.optional(),
    /**
     * Organisation slice — see `BrandsSchema`. Absent (or empty) in every
     * course authored before 2026-08-12, and that must keep meaning "no
     * Organisations, emit exactly what we emitted before".
     */
    brands: BrandsSchema.optional(),
    /**
     * The WORKSPACE logo — the client's own logo, chosen once in Settings >
     * Brand and carried into every package this workspace builds. Stored as
     * `asset://<uuid>` and rewritten by the packager to its in-package path.
     *
     * ⚠️ NOT the same thing as `brands[].logo` above, despite both being logos.
     *   · `brands[].logo` is a per-ORGANISATION logo, shown only while the
     *     learner has that organisation selected, and selected in CSS by
     *     `[data-brand="<code>"]`.
     *   · this is the workspace's own, shown whenever no organisation logo
     *     applies — including on a course with no organisations at all.
     * The organisation rules are more specific, so an organisation logo
     * continues to win wherever one applies; this replaces the logo the Player
     * ships hard-coded in its own stylesheet, which is BT's
     * (`feedback_identical_shape_different_semantics`).
     *
     * Absent means "no logo uploaded", and the package then keeps the Player's
     * own — never a blank space where a logo used to be.
     */
    workspaceLogo: UrlSchema.optional(),
    /**
     * The workspace brand's FIRST accent colour, injected server-side beside
     * `workspaceLogo`, and used for exactly one thing: the bar on the course's
     * initial loading screen.
     *
     * Omar, 2026-09-04: *"The progress/loading bar shown on the initial course
     * loading screen should no longer use a fixed colour. It should
     * automatically use the first Accent colour defined in the Brand section."*
     * The Player paints it `#5514b4` unconditionally
     * (`core_style.css` `#preloaderBarInner`).
     *
     * ⚠️ THIS IS A DELIBERATE, NARROW EXCEPTION to a rule that still holds
     * everywhere else: brand COLOURS are applied when a layout is CREATED, never
     * at build time, because reading them at build time would silently repaint
     * every existing course on its next export. That rule is about the course's
     * CONTENT. The loading bar is not content — it is chrome the author cannot
     * reach and has never been able to set — so nothing an author chose can be
     * overwritten by it. Keep the exception this narrow: one field, one colour,
     * one stylesheet rule. A second colour arriving here should be a decision,
     * not a follow-on.
     *
     * Absent means the org has no accent set, and the package then keeps the
     * Player's own purple — never a blank or transparent bar.
     */
    workspaceAccent: ColorSchema.optional(),
    /**
     * The workspace's seven Course-wide colours (Omar, 2026-10-21, wiki Brand_design §2
     * D24), injected server-side beside `workspaceLogo` like the accent above, which they
     * replace: the export paints them into the course's style sheet
     * (`@dynamo/scorm-packager` `course-wide-colours.ts`).
     *
     * ⚠️ The exception above WIDENS here, and it is his decision, not a follow-on: "a
     * course-wide colour applies to every course at the next preview or export, like the
     * logo and loading bar" (D22). These are chrome the author cannot reach from a course —
     * the loading screen, the assessment, the main menu's header, the home button — so
     * nothing an author chose in a course is overwritten by them. Layout colours stay
     * creation-time only.
     *
     * `workspaceAccent` stays accepted so content made before 2026-10-21 still parses; the
     * export reads it only when this is absent.
     */
    workspaceCourseWide: WorkspaceBrandCourseWideSchema.optional(),
})
    .strict();
// ── The roles ↔ organisations cross-slice rules used to live here ───────────
//
// ⚠️ MOVED 2026-08-12 (Phase 4b) to `organisation-gate.ts`, where they run at
// EXPORT instead of at save. Phase 4a put them in a `.superRefine` on the schema
// above, which put them on the `PUT /draft` path, and Phase 4b found the
// consequence by building the screen that produces the states: clicking "Add
// organisation" creates one with no roles yet, so the FIRST action of using the
// feature returned 400 and made the whole course unsavable — and `PUT /draft` is
// all-or-nothing, so unrelated text and media edits could not be saved either.
//
// The rule was already written down three files away (`routes/export.ts`: *"The
// schema stays permissive on saves so authors can checkpoint partial work;
// export is the strict gate."*). Saving is not shipping. See
// `organisation-gate.ts` for the checks and the runtime evidence behind them.
/**
 * The shape of `DraftVersion.content` (the JSONB column). Every draft version
 * stores a complete snapshot of the course body.
 */
export const DraftContentSchema = z
    .object({
    /** One or more module groups. v1 courses ship with a single group. */
    moduleGroups: z.array(ModuleGroupSchema).min(1),
    /** Optional course-level settings (DFTI flow, etc.). Absent for courses that don't use any settings-flagged features. */
    courseSettings: CourseSettingsSchema.optional(),
})
    .strict();
//# sourceMappingURL=course.js.map